import React, { useEffect, useRef, useState } from "react";
import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import { updateProgressAction } from "../../Actions/KnowledgeBaseAction";
import { getYoutubeId, getVimeoId, formatDuration } from "./videoMeta";
import "../../components/Common/FormModal.css";
import "./Knowledgebase.css";

const YOUTUBE_THUMB = "https://i.ytimg.com/vi";
const VIMEO_THUMB = "https://i.vimeocdn.com/video";

export const TutorialPlayerModal = ({ open, onClose, tutorial = {} }) => {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const lastSavedRatioRef = useRef(null);
  const [showFallbackVideo, setShowFallbackVideo] = useState(false);
  const [videoPlayerError, setVideoPlayerError] = useState(false);

  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const saveProgress = async (extra = {}) => {
    try {
      if (!tutorial?.id) return;
      let ratio = 0;
      let duration = tutorial?.duration || extra.duration || null;
      const element = videoRef.current;
      if (element && !Number.isNaN(element.duration) && element.duration > 0) {
        ratio = Math.max(0, Math.min(1, element.currentTime / element.duration));
        if (element.duration) duration = duration || formatDuration(Math.round(element.duration));
      } else if (extra.ratio != null) {
        ratio = Math.max(0, Math.min(1, Number(extra.ratio)));
      }
      const payload = {
        tutorialId: tutorial.id,
        moduleId: tutorial.moduleId || null,
        moduleSlug: tutorial.moduleSlug || null,
        watchedRatio: ratio,
        duration: duration || null,
        thumbnail: tutorial.thumbnail || null,
      };
      const shouldSave =
        extra.force === true ||
        lastSavedRatioRef.current == null ||
        Math.abs((lastSavedRatioRef.current ?? 0) - ratio) >= 0.05 ||
        (ratio >= 0.9 && (lastSavedRatioRef.current ?? 0) < 0.9);
      if (!shouldSave) return;
      lastSavedRatioRef.current = ratio;
      try {
        await updateProgressAction(payload);
      } catch (e) {
        console.warn("[KB progress] save error:", e?.message || e);
      }
    } catch (err) {
      console.warn("[KB progress] save error (outer):", err?.message || err);
    }
  };

  useEffect(() => {
    if (!open) {
      clearProgressInterval();
      lastSavedRatioRef.current = null;
      setShowFallbackVideo(false);
      setVideoPlayerError(false);
      return;
    }
    setShowFallbackVideo(false);
    setVideoPlayerError(false);
    const setup = () => {
      const element = videoRef.current;
      if (!element) {
        const timer = setTimeout(setup, 100);
        return () => clearTimeout(timer);
      }
      element.addEventListener("ended", () => saveProgress({ ratio: 1, force: true }));
      element.addEventListener("pause", () => saveProgress({ force: true }));
      element.addEventListener("error", () => {
        setVideoPlayerError(true);
      });
      progressIntervalRef.current = setInterval(() => saveProgress(), 10000);
      return () => {
        clearProgressInterval();
        element.removeEventListener("ended", () => saveProgress({ ratio: 1, force: true }));
        element.removeEventListener("pause", () => saveProgress({ force: true }));
      };
    };
    const cleanup = setup();
    return () => {
      if (typeof cleanup === "function") cleanup();
      clearProgressInterval();
      saveProgress({ force: true });
    };
    // eslint-disable-next-line
  }, [open, tutorial?.id]);

  const videos = Array.isArray(tutorial?.attachments)
    ? tutorial.attachments.filter(
        (a) => (a.mediaType || "").toLowerCase().startsWith("video"),
      )
    : [];
  const firstVideoAttachment = videos[0];
  const videoUrl = tutorial?.videoUrl || firstVideoAttachment?.url || "";
  const poster = tutorial?.thumbnail || firstVideoAttachment?.thumbnail ||
    (getYoutubeId(videoUrl) ? `${YOUTUBE_THUMB}/${getYoutubeId(videoUrl)}/hqdefault.jpg` : null);

  const ytId = getYoutubeId(videoUrl);
  const vimeoId = getVimeoId(videoUrl);
  const useVideoElement = !ytId && !vimeoId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="tutorial-player-title"
      PaperProps={{ className: "kb-player-dialog" }}
    >
      <div className="kb-player-header">
        <span className="fm-header-icon" aria-hidden>
          <VideocamIcon sx={{ fontSize: 20, color: "#fff" }} />
        </span>
        <div className="fm-header-copy">
          <h2 id="tutorial-player-title">
            {tutorial?.title || "Knowledge Base"}
          </h2>
        </div>
        <IconButton
          className="fm-close"
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <DialogContent sx={{ p: 0, bgcolor: "#0b1220" }}>
        <div className="kb-player-stage">
          <div className="kb-player-stage-inner">
            {!videoUrl && !showFallbackVideo && (
              <p className="kb-player-empty">
                No video URL attached to this Knowledge Base
              </p>
            )}
            {ytId && !showFallbackVideo && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                <iframe
                  ref={iframeRef}
                  title="YouTube Tutorial"
                  src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=0&modestbranding=1&rel=0`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onError={() => setShowFallbackVideo(true)}
                  style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                />
              </Box>
            )}
            {vimeoId && !showFallbackVideo && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                <iframe
                  ref={iframeRef}
                  title="Vimeo Tutorial"
                  src={`https://player.vimeo.com/video/${vimeoId}?byline=0&portrait=0`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  onError={() => setShowFallbackVideo(true)}
                  style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                />
              </Box>
            )}
            {useVideoElement && videoUrl && (
              <video
                ref={videoRef}
                controls
                poster={poster}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  background: "black",
                  display: videoPlayerError ? "none" : "block",
                }}
                onPlay={() => saveProgress()}
                onLoadedMetadata={() => saveProgress()}
                onError={() => {
                  setVideoPlayerError(true);
                  setShowFallbackVideo(true);
                }}
                preload="metadata"
              >
                {videoUrl && <source src={videoUrl} />}
              </video>
            )}
            {videoPlayerError && (
              <div className="kb-player-error">
                <h3>Unable to play video here</h3>
                <p>
                  Your browser / network blocked the video player.
                  <br />
                  Click the button below to open the video in a new tab instead.
                </p>
                {videoUrl && (
                  <a
                    className="kb-player-open"
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open video in new tab →
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

